<script setup lang="ts">
import { LocaleType, useLocalizationStore, useMemberStore, useNationStore, useSessionStore } from "@/store";
import { menuLanguages } from "@/configuration";
import { alert } from "@/services";
import { Authenticator } from "@/libs/R2ServiceClient/service/Auth";
import { useRouter } from "vue-router";
import { onMounted, ref } from 'vue';
import { Member, Role } from "@/models";
import { langStore, setLang } from "@/utils/langStore";
import { postLogout } from "@/api/auth/postLogout";
import { LOCAL_STORAGE_KEYS } from "@/constants/storageKeys";
import { getMe } from "@/api/auth/getMe";
import { GetMePartner, GetMePlayer } from "@/api/type";
import { localize } from "@/utils";

const router = useRouter();

const { setLanguage } = useLocalizationStore();
const { initNation } = useNationStore();
const { getMember, removeMember,setMember } = useMemberStore();

const member = getMember();

/**
 * 드롭다운 상태 변수
 */
const isPlaygroundDropdownOpen = ref(false);
const isSupportDropdownOpen = ref(false);
const isLangDropdownOpen = ref(false);

/**
 * 로그아웃
 */
// async function logout(): Promise<void> {
//   // console.log("logout - member.id", member.id);
//   removeMember();
//   closeMobileMenu();
//   router.push("/home/login");
//   const result = await Authenticator.logout();
//   if (!result) {
//     console.error("로그아웃 요청 실패");
//   }
// }
const token = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
const lang = ref(localStorage.getItem(LOCAL_STORAGE_KEYS.LANGUAGE) || 'en-US');
const logout = async () => {
  if(!token) return;
  try {
    const res = await postLogout();
    if(res.isSuccess) {
      removeMember();
      closeMobileMenu();
      router.push("/home/login");
    } else {
      console.log("로그아웃 요청 실패")
    }
  } catch (err) {
    alert(localize('서버 연결에 실패하였습니다.<br/>잠시후, 다시 시도해 주세요.'));
  }
}

async function fetchMember() {
  if (!token) return;
  try {
    const res = await getMe();
    if (res.isSuccess) {
      const apiData = res.data;

      const memberPartial: Partial<Member> = {
        role: apiData.role,
        id: apiData.id,
        loginId: apiData.loginId,
        name: {
          firstName: apiData.name.firstName,
          lastName: apiData.name.lastName,
          fullName: lang.value === "ko-KR" ? `${apiData.name.lastName} ${apiData.name.firstName}` : `${apiData.name.firstName} ${apiData.name.lastName}`,
        },
        roles: apiData.role === 'PLAYER' ? [Role.PLAYER] : [Role.PLAYER, apiData.role as Role],
      };

    setMember(memberPartial as Member); 
    } else {
      // console.error('API 호출 실패', res.error.code);
    }
  } catch (err) {
    console.error('API 호출 실패', err);
  }
}

/**
 * 모바일이거나 화면이 작을 경우의 전체 메뉴 열기/닫기
 */
function toggleMobileMenu(): void {
  document.body.classList.toggle("open-gnb-menu");
}
/**
 * 모바일이거나 화면이 작을 경우의 전체 메뉴 닫기
 */
function closeMobileMenu(): void {
  document.body.classList.remove("open-gnb-menu");
}
/**
 * 언어 변경
 * @param locale
 * @param event
 */

function changeLocale(locale: LocaleType, event: Event): void {
  lang.value = locale;
  setLanguage(locale);
  setLang(locale)
  initNation(locale);
  closeMobileMenu();
  closeLangDropdown();
}

/**
 * 드롭다운 열기/닫기
 */
 function openPlaygroundDropdown(): void {
  isPlaygroundDropdownOpen.value = true;
}

function closePlaygroundDropdown(): void {
  isPlaygroundDropdownOpen.value = false;
}

/**
 * 고객지원 드롭다운 열기/닫기
 */
 function openSupportDropdown(): void {
  isSupportDropdownOpen.value = true;
}

function closeSupportDropdown(): void {
  isSupportDropdownOpen.value = false;
}

/**
 * 언어 선택 드롭다운 열기/닫기
 */
 function openLangDropdown(): void {
  isLangDropdownOpen.value = true;
}

function closeLangDropdown(): void {
  isLangDropdownOpen.value = false;
}

onMounted(() => {
  fetchMember();
});

const partnerWebUrl = import.meta.env.VITE_PARTNER_WEB_URL

</script>
<template>
  <header id="hd">
    <div class="container container-fluid">
      <!-- 사이트 로고 -->
      <router-link to="/home" class="logo text-hide">R2 Playground</router-link>
      <!-- //사이트 로고 -->

      <nav id="gnb">
        <ul class="gnb">
          <!-- R2 Playground 메뉴 --> 
          <li class="dropdown" @mouseover="openPlaygroundDropdown" @mouseleave="closePlaygroundDropdown">
            <router-link to="/home/playground" @click="closeMobileMenu">R2 Playground</router-link>
            <ul class="sub-menu" :class="{ open: isPlaygroundDropdownOpen }">
              <li><router-link to="/home/playground/player" @click="() => {closeMobileMenu(); closePlaygroundDropdown();}">R2 Player</router-link></li>
              <li><router-link to="/home/playground/planner" @click="closeMobileMenu">R2 Planner</router-link></li>
              <li><router-link to="/home/playground/r2center" @click="closeMobileMenu">R2 Center</router-link></li>
            </ul>
          </li>
          <!-- //R2 Playground 메뉴 -->

          <!-- R2 Solution 링크 -->
          <li><router-link to="/home/solution" @click="closeMobileMenu">R2 Solution</router-link></li>
          <!-- //R2 Solution 메뉴 -->

          <!-- 고객지원 메뉴 -->
          <li class="dropdown" @mouseover="openSupportDropdown" @mouseleave="closeSupportDropdown">
            <router-link to="/home/support" @click="closeMobileMenu">Community</router-link>
            <ul class="sub-menu" :class="{ open: isSupportDropdownOpen }">
              <li><router-link to="/home/support/faq" @click="() => {closeMobileMenu(); closeSupportDropdown();}">FAQ</router-link></li>
              <!-- <li><router-link to="/home/support/inquiry-save" @click="() => {closeMobileMenu(); closeSupportDropdown();}">1:1 inquiry</router-link></li> -->
            </ul>
          </li>
          <!-- //고객지원 메뉴 -->
        </ul>
        <p class="academy" v-if="member.id !== null">
          <!-- 로그인 상태 -->
          <template v-if="member.role === 'PLAYER'">
            <div style="font-size: 0.8rem; font-weight: 500; border-radius: 5px; background-color: #33333315; padding: 4px;">
              {{ lang === 'ko-KR' ? `환영합니다. ${member.name.firstName}님` : `Welcome, ${member.name.firstName}` }}
            </div>
          </template>
          <template v-else>
            <!-- 파트너 홈 링크 -->
            <a
              :href="`${partnerWebUrl}/#/${lang === 'ko-KR' ? 'ko' : 'en'}/login`"
              target="_blank"
              rel="noopener noreferrer"
              @click="closeMobileMenu"
              style="font-size: 0.8rem; font-weight: 500; padding: 4px;">
              {{ lang === 'ko-KR' ? '파트너 홈' : 'Partner Home' }}
            </a>
            
          </template>
          <router-link class="auth-link" v-if="member.id != null" to="/home/login" @click="logout">Log Out</router-link>
        </p>

        <div class="hd-util">
          <!-- 로그인 / 로그아웃 -->
          <router-link class="auth-link" v-if="member.id === null" to="/home/login" @click="closeMobileMenu">Log in</router-link>
          <!-- 회원가입 -->
          <router-link class="auth-link" v-if="member.id === null" to="/home/join" @click="closeMobileMenu">Sign up</router-link>
          <!-- //회원가입 -->

          <!-- 언어 선택 -->
          <div id="custom-dropdown" class="dropdown lang" @mouseover="openLangDropdown" @mouseleave="closeLangDropdown">
            <a class="icon icon-lang"></a>
            <ul class="sub-menu" :class="{ open: isLangDropdownOpen }">
              <li v-for="item in menuLanguages">
                <a href="#" @click="changeLocale(item.value as LocaleType, $event)">{{ item.label }}</a>
              </li>
            </ul>
          </div>
          <!-- //어 선택 -->

          <!-- 디지털 도서관 -->
          <button class="btn btn-secondary btn-sm">
            <a href="https://r2gate.com/cad-cam?cate=3shape" target="_blank"><span>Digital Library</span><i class="icon icon-arrow"></i></a>
          </button>
          <!-- //디지털 도서관 -->
        </div>
      </nav>
      <button type="button" class="menu-open" @click="toggleMobileMenu">menu</button>
    </div>
  </header>
</template>

<style scoped>
/**
 * 연산자 우선순위 높이기 위해 id값 설정
 */
.custom-dropdown .sub-menu {
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
}

.custom-dropdown .sub-menu.open {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}
.disabled {
  opacity: 0.25 !important;
}
.auth-link {
  font-size: 0.9rem;
  font-weight: 500;
  text-decoration: none;
}

</style>
